import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, ShoppingBag } from "lucide-react";
import { shopApi, productLabel } from "@/lib/shopApi";
import { money } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { QtyStepper } from "@/pages/store/ProductDetail";

export function SummaryRow({ label, value, strong, testid, accent }) {
  return (
    <div className={`flex items-center justify-between ${strong ? "border-t border-slate-200 pt-3 text-base font-bold text-slate-900" : "text-sm text-slate-600"}`}>
      <span>{label}</span><span className={accent ? "font-semibold text-sky-700" : ""} data-testid={testid}>{value}</span>
    </div>
  );
}

export default function Cart() {
  const { cart, setQty } = useMember();
  const [info, setInfo] = useState(null);
  useEffect(() => { shopApi.get("/shop/store-info").then((r) => setInfo(r.data)).catch(() => {}); }, []);
  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const pct = info?.auto_discount_percent || 0;
  const auto = Math.round(subtotal * pct) / 100;

  if (!cart.length)
    return (
      <div className="mx-auto max-w-md py-16 text-center" data-testid="cart-empty">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-50 text-sky-700"><ShoppingBag className="h-6 w-6" /></div>
        <h1 className="mt-5 font-head text-2xl font-extrabold">Your cart is empty</h1>
        <Link to="/shop" className="mt-6 inline-flex h-11 items-center rounded-lg bg-[#89CFF0] px-6 font-semibold text-slate-900 hover:bg-[#74C2EC]" data-testid="cart-browse-btn">Browse the shop</Link>
      </div>
    );

  return (
    <div data-testid="cart-page">
      <h1 className="font-head text-4xl font-extrabold tracking-tight">Cart</h1>
      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px]">
        <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white">
          {cart.map((i) => (
            <div key={i.product_id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5" data-testid={`cart-item-${i.product_id}`}>
              <div>
                <div className="font-semibold text-slate-900" data-testid={`cart-item-name-${i.product_id}`}>{productLabel(i)}</div>
                <div className="text-sm text-slate-500">{money(i.price)} each</div>
              </div>
              <div className="flex items-center gap-4">
                <QtyStepper value={i.qty} onChange={(v) => setQty(i.product_id, v)} testid={`cart-qty-${i.product_id}`} />
                <span className="w-20 text-right font-semibold">{money(i.price * i.qty)}</span>
                <button onClick={() => setQty(i.product_id, 0)} className="text-slate-400 transition-colors hover:text-rose-600" data-testid={`cart-remove-${i.product_id}`} aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
        <aside className="h-fit space-y-3 rounded-xl border border-slate-200 bg-white p-5 lg:sticky lg:top-24" data-testid="cart-summary">
          <SummaryRow label="Subtotal" value={money(subtotal)} testid="cart-subtotal" />
          {pct > 0 && <SummaryRow label={`Bank transfer discount (${pct}%)`} value={`-${money(auto)}`} testid="cart-auto-discount" accent />}
          <SummaryRow label="Shipping (flat rate)" value={money(info?.shipping_cost)} testid="cart-shipping" />
          <SummaryRow label="Estimated total (AUD)" value={money(subtotal - auto + (info?.shipping_cost || 0))} strong testid="cart-total" />
          <p className="text-xs text-slate-500">Discount codes are applied at checkout.</p>
          <Link to="/checkout" className="flex h-11 w-full items-center justify-center rounded-lg bg-[#89CFF0] font-semibold text-slate-900 transition-colors hover:bg-[#74C2EC]" data-testid="cart-checkout-btn">Proceed to checkout</Link>
        </aside>
      </div>
    </div>
  );
}
